/**
 * ChatInput Component
 * Input area with textarea, model selector, file attachments with drag & drop
 */

import { useState, useRef, useEffect, useCallback, type ReactNode } from 'react';
import { message as antdMessage } from 'antd';
import { useLanguage } from '@hooks/useLanguage';
import { useFeature } from '@hooks/useFeature';
import { cachedGet, peekCachedGet } from '@services/cachedGet';
import { agentModelScope, modelCatalogUrl, showModelPicker } from '@/utils/modelPicker';
import type { CatalogContext } from '@/utils/modelPicker';
import { providerOfModel } from '@/utils/modelProvider';
import {
  validateFileForModel,
  getGlobalLimits,
  isFileTypeBlocked,
  getAcceptAttribute,
  showUploadErrorModal,
  UploadError,
} from '../services/fileValidationService';
import { ModelSelector, findModelEntry } from './ModelSelector';
import { ArtifactSettings, type BrandableFormat } from './ArtifactSettings';
import { VoiceInputButton } from './VoiceInputButton';
import { ModeMenuButton } from './ModeMenuButton';
import { FileAttachmentCard } from './FileAttachmentCard';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { emitJourneyEvent, JOURNEY_EVENTS } from '@modules/journey/detect';
import { AttachMenu } from './drive/AttachMenu';
import { useDriveConnected } from '../hooks/useDriveConnected';
import { DriveFilePicker } from './drive/DriveFilePicker';
import type { DriveFileRef } from '@/types/models';
import { draftToRestore, type SendOutcome } from '../utils/returnedDraft';
import './ChatInput.css';

interface ChatInputProps {
  /** May answer `'not_taken'` (see returnedDraft): the server never took the
   *  message, so the composer puts the words it just cleared back. */
  onSendMessage: (content: string, attachments?: File[], driveFiles?: DriveFileRef[]) => void | Promise<SendOutcome>;
  textareaDisabled?: boolean;
  sendDisabled?: boolean;
  selectedModel: string;
  onModelChange: (model: string) => void;
  disableModelPicker?: boolean;
  disableAttachments?: boolean;
  /** Emit journey detections — "a message was sent", "a file was attached",
   *  "the model was switched" — from this composer. **Opt IN, default off.**
   *
   *  This box is embedded five times: the real chat, the Concierge, the
   *  Agent-of-Agents builder, the agent simulation panel, and the Guide on
   *  the Help page. Only the first two are a person USING the product; in
   *  the other three they are building an agent, test-running someone
   *  else's, or asking about the platform — none of which should complete a
   *  step of their tour. It started as an opt-out and three of the five
   *  embeds silently got it wrong, so the default is now silence and the two
   *  real conversations say so explicitly. */
  journeyDetection?: boolean;
  /** Whether THIS composer carries Drive picks through to its send path.
   *  Default true. A surface whose `onSendMessage` drops the third argument
   *  (the meta-agent's simulation panel) sets false, so the Drive entry is
   *  not offered and silently dropped. This is a property of the surface,
   *  never of the agent: the agent's own Drive switch governs its TOOLS and
   *  has no say over what a person attaches (operator decision 2026-09-28). */
  driveAttachments?: boolean;
  allowedModels?: string | string[];
  /** Whose pins name the models in the picker — the conversation's tenant
   *  (utils/modelPicker `CatalogContext`). */
  modelCatalogContext?: CatalogContext;
  initialValue?: string;
  forceValueUpdate?: number;  // Timestamp to force value update (for edit mode)
  isStreaming?: boolean;
  onStopGeneration?: () => void;
  /** Render Stop BESIDE the send button instead of replacing it. For composers
   *  that keep Send live during a turn (the meta-agent queues messages
   *  mid-turn), where swapping Send out would take that away. */
  showStopBesideSend?: boolean;
  isEditing?: boolean;
  onCancelEdit?: () => void;
  droppedFiles?: File[] | null;
  onDropProcessed?: () => void;
  quotedText?: string;
  onClearQuote?: () => void;
  /** Puts a reply's quotation back when a send was not taken. */
  onRestoreQuote?: (quote: string) => void;
  // Mobile mode picker — when supplied, renders the ModeMenuButton chip
  // inside the input toolbar. The chip is CSS-gated to mobile widths only,
  // so passing these props on desktop is a no-op (the inline slider in
  // ConversationPanel's header continues to be the sole desktop control).
  modeConfig?: {
    enabled: boolean;
    title: string;
    description: string;
    default_index: number;
    modes: Array<{ label: string; description?: string; instruction: string }>;
  };
  conversationIdForMode?: string;
  activeModeIndex?: number;
  onModeChange?: (index: number) => void;
  // Artifact branding toggles — rendered only when supplied with at least one
  // format (the parent gates on: agent can produce pptx/docx, tenant has the
  // artifact module on, AND a live brand book exists).
  artifactSettings?: {
    formats: BrandableFormat[];
    branding: Record<string, boolean>;
    onToggle: (format: BrandableFormat, branded: boolean) => void;
  };
  // The context meter chip (ContextMeter) — sits at the composer's trailing
  // edge beside the send button, the way Claude places it. The parent owns
  // the reading and the "Compact now" handler; this is only its place.
  contextMeter?: ReactNode;
  // The connectors control (connectors/AgentConnectors) — the person's own
  // per-agent switches, Google Drive for now. A sibling of the model picker
  // and artifact settings; the parent supplies it only for an agent that
  // allows a connector, so nothing is drawn otherwise.
  connectors?: ReactNode;
}

interface Model {
  id: string;
  name: string;
  provider: string;
  description?: string;
  /** Concrete pinned version behind this family — READ-ONLY display info. */
  resolved_model?: string;
  resolved_model_name?: string;
}

/** How long a `/ai/models/` answer is shared between composers. */
const MODELS_TTL_MS = 5 * 60_000;

/** The models in a `/ai/models/` answer. */
function catalogModels(data: any): Model[] {
  return data.models || data;
}

/**
 * File with validation status for display
 */
interface FileWithStatus {
  file: File;
  status: 'pending' | 'valid' | 'invalid';
  error?: string;
}

// File-type icon + size formatting are now owned by FileAttachmentCard
// (Anthropic-style cards with SVG icons and image thumbnails — no emojis).

export function ChatInput({
  onSendMessage,
  textareaDisabled = false,
  sendDisabled = false,
  selectedModel,
  onModelChange,
  disableModelPicker = false,
  disableAttachments = false,
  journeyDetection = false,
  driveAttachments = true,
  allowedModels = 'all',
  modelCatalogContext,
  initialValue = '',
  forceValueUpdate,
  isStreaming = false,
  onStopGeneration,
  showStopBesideSend = false,
  isEditing = false,
  onCancelEdit,
  droppedFiles,
  onDropProcessed,
  quotedText,
  onClearQuote,
  onRestoreQuote,
  modeConfig,
  conversationIdForMode,
  activeModeIndex,
  onModeChange,
  artifactSettings,
  contextMeter,
  connectors,
}: ChatInputProps) {
  const { t, language, direction } = useLanguage();
  const voiceInputEnabled = useFeature('chat.voice_input');
  // Mobile mode-picker chip is gated by the same flag that gates the
  // desktop ModeSelector slider — both are surfaces of the
  // behavior-switching feature, so they must rise and fall together.
  const behaviorSwitchingEnabled = useFeature('agents.behavior_switching');
  const [message, setMessage] = useState(initialValue);
  // What is in the box NOW, for a send that is refused after the box emptied.
  const messageRef = useRef(message);
  messageRef.current = message;
  const [attachments, setAttachments] = useState<FileWithStatus[]>([]);
  // Google Drive picks live beside local files until send; the backend
  // fetches them by id with the user's own Drive token.
  const [driveFiles, setDriveFiles] = useState<DriveFileRef[]>([]);
  const [drivePickerOpen, setDrivePickerOpen] = useState(false);
  // The composer only changes shape for someone who actually has Drive
  // connected. The tenant flag is the ceiling; the connection is what makes
  // the menu appear, so a user who never linked an account keeps the
  // one-click paperclip they have always had. The AGENT's Drive switch takes
  // no part (operator decision 2026-09-28): attaching a file from Drive is
  // an upload with the person's own token, not an agent tool.
  const driveAvailable = useFeature('integrations.cloud_storage') && driveAttachments;
  const driveEnabled = useDriveConnected(driveAvailable);
  // The agent's scope and its `/ai/models/` url. The url carries the
  // allowed-models filter and is the cache key, so two agents with different
  // allow-lists don't share a result.
  const modelScope = agentModelScope(allowedModels);
  const modelsUrl = modelCatalogUrl(modelScope, modelCatalogContext);
  // A composer that mounts after the catalogue is cached starts from it, so
  // its first frame already knows whether there is a choice to offer.
  const [availableModels, setAvailableModels] = useState<Model[]>(() => {
    const cached = peekCachedGet<any>(modelsUrl, MODELS_TTL_MS);
    return cached ? catalogModels(cached) : [];
  });
  // Which url `availableModels` answers — null until the first answer.
  const [modelsLoadedFor, setModelsLoadedFor] = useState<string | null>(
    () => (peekCachedGet(modelsUrl, MODELS_TTL_MS) ? modelsUrl : null),
  );
  const [isDragging, setIsDragging] = useState(false);
  const [maxFiles, setMaxFiles] = useState(10);
  const [acceptAttribute, setAcceptAttribute] = useState<string>('');
  // Live voice-input buffers. Two distinct things during a recording:
  //
  //   pendingFinals — chunks the recogniser HAS finalised, but we do
  //     NOT yet commit them to the textarea. They stay in the italic
  //     mirror overlay until the user actually stops the recording.
  //     User feedback (2026-06-09): "I want that the text will become
  //     black like a real text the moment I stop the record. Till
  //     then it will still be like the writing text."
  //
  //   interimText — the in-flight chunk the recogniser hasn't yet
  //     finalised. Replaces on every onInterimTranscript callback
  //     (Web Speech API emits CUMULATIVE interims, not deltas).
  //
  // Both are rendered in the same italic+dimmed style inside the
  // mirror. The flush — italic → real text — happens only when
  // voice.state transitions out of 'listening'.
  const [interimText, setInterimText] = useState('');
  const [pendingFinals, setPendingFinals] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // Mirror div that overlays the textarea, painted only with the
  // interim suffix. The committed value's chars are repeated as
  // visibility:hidden so the interim text appears at the right pixel
  // offset (after the committed text) with correct wrapping.
  const mirrorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  /**
   * Insert a finalized voice transcript at the textarea's current
   * selection (caret) — same behaviour as a user typing into a focused
   * input. If the textarea ref isn't available we fall back to
   * appending at the end so a transcript is never lost.
   *
   * Spacing rule: pad with a single space on either side only when the
   * neighbour is a non-whitespace character. Avoids "hello.world" runs
   * but doesn't double up on existing spaces.
   *
   * After insertion we restore the caret to the END of the inserted
   * text, so consecutive utterances chain forward instead of
   * back-stacking on top of each other.
   */
  const handleVoiceTranscript = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const ta = textareaRef.current;
    if (!ta) {
      setMessage((prev) => prev ? `${prev.trimEnd()} ${trimmed}` : trimmed);
      return;
    }
    const value = ta.value;
    const selStart = ta.selectionStart ?? value.length;
    const selEnd = ta.selectionEnd ?? value.length;
    const before = value.slice(0, selStart);
    const after = value.slice(selEnd);
    const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
    const needsSpaceAfter = after.length > 0 && !/^\s/.test(after);
    const insert = `${needsSpaceBefore ? ' ' : ''}${trimmed}${needsSpaceAfter ? ' ' : ''}`;
    const next = before + insert + after;
    setMessage(next);
    // React updates the textarea value asynchronously; restore the
    // selection on the next frame so the browser sees the new value.
    const newCaret = before.length + insert.length;
    requestAnimationFrame(() => {
      const ta2 = textareaRef.current;
      if (!ta2) return;
      try {
        ta2.setSelectionRange(newCaret, newCaret);
        ta2.focus();
      } catch { /* contenteditable quirks — ignore */ }
    });
  }, []);

  // Voice-input lifted from VoiceInputButton so the textarea can
  // observe `state` (to auto-stop on typing) and call `stop()` directly.
  // The button is now a presentational component that receives state
  // + start/stop/levels/duration as props.
  const handleVoiceError = useCallback((code: string) => {
    if (code === 'not-allowed' || code === 'service-not-allowed') {
      antdMessage.error(t('chat.voice.permissionDenied', {
        defaultValue: 'Microphone permission was denied. Enable it in your browser settings to use voice input.',
      }));
    } else {
      antdMessage.warning(t('chat.voice.error', {
        defaultValue: 'Voice input is unavailable right now.',
      }));
    }
  }, [t]);

  // Live-transcription handler — replaces the prior interim each
  // call. The Web Speech API emits a CUMULATIVE interim (not a delta),
  // so we just store the latest string. Stays in the italic overlay
  // until the user stops the recording.
  const handleVoiceInterim = useCallback((text: string) => {
    setInterimText(text);
  }, []);

  // Final-chunk handler — DOES NOT commit to the textarea. The
  // recogniser emits finals at natural pauses, but the user explicitly
  // asked (2026-06-09) that nothing transition to "real text" until
  // they stop the recording. So we accumulate finals into pendingFinals
  // (italic in the mirror) and let the listening→idle effect below
  // flush everything into the textarea at the end of the session.
  const handleVoiceFinal = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setPendingFinals((prev) => (prev ? `${prev} ${trimmed}` : trimmed));
    setInterimText('');
  }, []);

  const voice = useVoiceInput({
    onFinalTranscript: handleVoiceFinal,
    onInterimTranscript: handleVoiceInterim,
    onError: handleVoiceError,
    language: language === 'he' ? 'he' : 'en',
  });

  // Flush on listening → idle. ONE place handles the italic → real
  // text transition: when the user clicks stop, the 60s cap fires, or
  // the browser ends the session. Any accumulated pendingFinals + a
  // possibly-still-in-flight interimText get combined into one final
  // string and inserted at the caret via handleVoiceTranscript (same
  // path a typed message goes through).
  useEffect(() => {
    if (voice.state === 'listening') return;
    if (!pendingFinals && !interimText) return;
    const combined = (
      pendingFinals + (interimText ? (pendingFinals ? ' ' : '') + interimText : '')
    ).trim();
    if (combined) {
      handleVoiceTranscript(combined);
    }
    setPendingFinals('');
    setInterimText('');
  }, [voice.state, pendingFinals, interimText, handleVoiceTranscript]);

  // When the user STARTS a new recording, clear any stale buffer from a
  // prior session (defensive — should already be empty from the flush
  // above, but belt-and-suspenders for race-y browser behaviour).
  useEffect(() => {
    if (voice.state === 'listening') {
      setPendingFinals('');
      setInterimText('');
    }
    // Only when transitioning INTO 'listening'.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voice.state]);

  // Keep the mirror overlay's scrollTop in sync with the textarea so
  // the interim suffix stays visually aligned when the textarea
  // overflows and scrolls.
  useEffect(() => {
    const ta = textareaRef.current;
    const mirror = mirrorRef.current;
    if (!ta || !mirror) return;
    const onScroll = () => { mirror.scrollTop = ta.scrollTop; };
    ta.addEventListener('scroll', onScroll);
    return () => ta.removeEventListener('scroll', onScroll);
  }, []);

  // Check if any files are invalid (blocks sending)
  const hasInvalidFiles = attachments.some(att => att.status === 'invalid');

  // Normalize the selected ref to its family catalog entry. Conversations
  // may still hand us a legacy concrete id ('claude-sonnet-4-5-20250929');
  // the catalog entry gives us the stable family key + real provider.
  const activeModelEntry = findModelEntry(availableModels, selectedModel);
  // Family key used for client-side file validation — the capabilities map
  // from /ai/models/ is keyed by family ids, so a legacy stored ref must be
  // normalized before lookup. Unresolvable refs pass through unchanged
  // (validation fails open; the backend validates authoritatively).
  const validationModelRef = activeModelEntry?.id ?? selectedModel;
  // The provider the composer will send with, from the same catalogue entry
  // — what the file picker's filter AND the size / type checks read. A family
  // key's spelling says nothing ('flash' is Google); null while unknown, and
  // the files are judged again when the catalogue answers.
  const activeProvider = providerOfModel(availableModels, selectedModel);

  // Load global limits on mount
  useEffect(() => {
    getGlobalLimits().then(limits => {
      setMaxFiles(limits.max_files_per_message);
    });
  }, []);

  // Resolve the OS file-picker accept attribute from the active model's
  // provider so users can't see (and so can't easily pick) types we'll
  // reject server-side. Refreshed when the model or its provider changes.
  useEffect(() => {
    let cancelled = false;
    getAcceptAttribute(activeProvider).then((accept) => {
      if (!cancelled) setAcceptAttribute(accept);
    });
    return () => {
      cancelled = true;
    };
  }, [activeProvider]);

  /**
   * Re-validate all files when the model changes — or when the catalogue
   * answers and names its provider.
   */
  useEffect(() => {
    if (attachments.length > 0) {
      revalidateAllFiles();
    }
  }, [validationModelRef, activeProvider]);

  const revalidateAllFiles = async () => {
    const validated = await Promise.all(
      attachments.map(async (att, index) => {
        const result = await validateFileForModel(att.file, validationModelRef, activeProvider, index);
        return {
          file: att.file,
          status: result.status,
          error: result.error
        };
      })
    );
    setAttachments(validated);
  };

  // Update message when initialValue changes or forceValueUpdate triggers
  useEffect(() => {
    if (initialValue !== undefined) {
      setMessage(initialValue);
      // Focus textarea when editing
      if (forceValueUpdate && textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [initialValue, forceValueUpdate]);

  // Process files dropped on the parent (full-page drop zone)
  useEffect(() => {
    if (droppedFiles && droppedFiles.length > 0) {
      processFiles(droppedFiles);
      onDropProcessed?.();
    }
  }, [droppedFiles]);

  /**
   * Load available models from backend.
   *
   * Depend on the url — a STABLE STRING — not the `allowedModels` array
   * reference. Parents often build `allowedModels` inline (e.g.
   * `bot?.handler_config?.allowed_models || 'all'`), producing a fresh array
   * every render — which used to re-run this fetch on every parent render,
   * flipping `loadingModels` and remounting the model picker (the "it keeps
   * re-rendering one more element" + dropped-keystrokes symptom). Keying on
   * the url means we fetch once per actual allowed-set change; an answer for
   * a scope the composer has since left is dropped.
   */
  useEffect(() => {
    let cancelled = false;
    cachedGet<any>(modelsUrl, MODELS_TTL_MS)
      .then((data) => {
        if (cancelled) return;
        setAvailableModels(catalogModels(data));
        setModelsLoadedFor(modelsUrl);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('[ERROR] Failed to load models:', error);
        setAvailableModels([]);
        setModelsLoadedFor(modelsUrl);
      });
    return () => {
      cancelled = true;
    };
  }, [modelsUrl]);

  // One model is no choice: the picker is not rendered at all (see
  // utils/modelPicker.ts). Decided on the FINAL list for this scope — the
  // server's answer, or the cached one while a new scope's answer is on its
  // way; with neither yet, the scope alone decides.
  const finalModelsKnown = modelsLoadedFor === modelsUrl;
  const cachedScopeModels = finalModelsKnown ? undefined : peekCachedGet<any>(modelsUrl, MODELS_TTL_MS);
  const modelPickerShown = !disableModelPicker && showModelPicker(
    modelScope,
    finalModelsKnown ? availableModels : cachedScopeModels ? catalogModels(cachedScopeModels) : null,
  );

  /**
   * Auto-resize textarea as the user types OR as voice-input content
   * arrives in the mirror overlay.
   *
   * Pre-fix this depended only on [message]. When the user dictated
   * via the mic, the mirror overlay accumulated text but the textarea
   * stayed at single-row height — the mirror is `position: absolute;
   * inset: 0` so it's clipped to the textarea's height, and everything
   * past the first line vanished off-screen.
   *
   * Fix: the hook also re-runs when interimText / pendingFinals change,
   * and uses the LARGER of `textarea.scrollHeight` (committed text)
   * and `mirror.scrollHeight` (committed + finals + interim). Because
   * scrollHeight reports the CONTENT size even when the element is
   * clipped by overflow, the mirror's full unwrapped content size is
   * available for sizing — and the textarea grows to fit.
   */
  useEffect(() => {
    if (!textareaRef.current) return;
    const textarea = textareaRef.current;
    const mirror = mirrorRef.current;

    textarea.style.height = 'auto';
    textarea.style.overflowY = 'hidden';

    let needed = textarea.scrollHeight;

    // Mirror is only rendered while interimText OR pendingFinals are
    // non-empty (see JSX below). When present, its content is the full
    // combined transcript (committed text invisible + italic finals +
    // italic interim). Take whichever is larger so the textarea grows
    // to fit the longest of the two layouts.
    if (mirror && (interimText || pendingFinals)) {
      needed = Math.max(needed, mirror.scrollHeight);
    }

    const maxHeight = 200;
    if (needed > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.height = `${needed}px`;
    }
  }, [message, interimText, pendingFinals]);

  /**
   * iOS form accessory toolbar (↑ ↓ ✓) shows "up/down" arrows when iOS
   * thinks there's more than one focusable form field. Pulling every
   * other input/textarea/select out of the tab order while the chat
   * textarea is focused convinces iOS that this is the only field, so
   * only "Done" remains. On blur we restore the original tabindex so
   * side-drawer search boxes etc. remain keyboard-reachable.
   */
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    let saved: Array<{ el: HTMLElement; prev: string | null }> = [];

    const isolate = () => {
      const fields = document.querySelectorAll<HTMLElement>(
        'input:not([type="hidden"]), textarea, select'
      );
      saved = [];
      fields.forEach((el) => {
        if (el === textarea) return;
        saved.push({ el, prev: el.getAttribute('tabindex') });
        el.setAttribute('tabindex', '-1');
      });
    };

    const restore = () => {
      saved.forEach(({ el, prev }) => {
        if (prev === null) el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', prev);
      });
      saved = [];
    };

    textarea.addEventListener('focus', isolate);
    textarea.addEventListener('blur', restore);
    return () => {
      textarea.removeEventListener('focus', isolate);
      textarea.removeEventListener('blur', restore);
      restore();
    };
  }, []);

  /**
   * Handle send message
   */
  const handleSend = () => {
    // Allow sending with attachments only (no typed message) — a files-only
    // message is valid. Still block an empty send with no files, and never
    // send while a file failed validation.
    const hasAnyFile = attachments.length > 0 || driveFiles.length > 0;
    if ((!message.trim() && !hasAnyFile) || sendDisabled || hasInvalidFiles) {
      return;
    }

    // Prepend quoted text as a reply reference if present
    const fullContent = quotedText
      ? `[reply: "${quotedText}"]\n${message}`
      : message;

    // Extract just the File objects from FileWithStatus
    const filesToSend = attachments.length > 0
      ? attachments.map(att => att.file)
      : undefined;

    const sent = { message, quote: quotedText ?? null };
    const outcome = onSendMessage(fullContent, filesToSend, driveFiles.length > 0 ? driveFiles : undefined);
    // The box empties below, at once. A send the server never took (a refused
    // attachment) gives the words back — unless new ones were typed meanwhile.
    if (outcome instanceof Promise) void outcome.then((result) => {
      if (result !== 'not_taken') return;
      const draft = draftToRestore(sent, messageRef.current);
      if (!draft) return;
      setMessage(draft.message);
      if (draft.quote) onRestoreQuote?.(draft.quote);
    });
    // Journey detections, inside the one funnel that actually sends: a
    // message went out, and (separately) a message with a file went out.
    // Only the embeds that ARE the product opt in (see the prop), so
    // building or testing an agent — or asking about the platform — never
    // counts as using it.
    if (journeyDetection) {
      emitJourneyEvent(JOURNEY_EVENTS.messageSent);
      if (filesToSend && filesToSend.length > 0) {
        emitJourneyEvent(JOURNEY_EVENTS.fileAttached, { count: filesToSend.length });
      }
    }
    setMessage('');
    setAttachments([]);
    setDriveFiles([]);
    onClearQuote?.();

    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /**
   * Process and validate files (used by both file input and drag & drop)
   * Now validates each file individually and adds with status
   */
  const processFiles = async (files: File[]) => {
    if (files.length === 0) return;

    // Pre-flight: filter out deferred-types (xlsx/csv/pptx/...) BEFORE
    // anything else. Show the friendly modal once per batch — the user
    // sees the "we're working on it" copy with the exact list of types
    // we don't support yet, instead of silently dropping the files.
    const blockedChecks = await Promise.all(
      files.map(async (f) => ({ file: f, blocked: await isFileTypeBlocked(f) })),
    );
    const blocked = blockedChecks.filter((c) => c.blocked).map((c) => c.file);
    const allowed = blockedChecks.filter((c) => !c.blocked).map((c) => c.file);
    if (blocked.length > 0) {
      showUploadErrorModal(
        new UploadError({
          code: 'FILE_TYPE_NOT_SUPPORTED_YET',
          message: '',
          details: { file_name: blocked.map((f) => f.name).join(', ') },
        }),
        t,
      );
    }
    if (allowed.length === 0) return;
    files = allowed;

    // Check global file limit (Drive picks count toward the same cap)
    const currentCount = attachments.length + driveFiles.length;
    if (currentCount >= maxFiles) {
      antdMessage.error({
        content: t('fileUpload.errors.tooManyFiles', { limit: maxFiles }),
        duration: 5
      });
      return;
    }

    // Limit files to add to stay within max
    const remainingSlots = maxFiles - currentCount;
    const filesToAdd = files.slice(0, remainingSlots);

    if (filesToAdd.length < files.length) {
      antdMessage.warning({
        content: t('fileUpload.errors.tooManyFiles', { limit: maxFiles }),
        duration: 3
      });
    }

    // Validate each file and create status objects
    const newFilesWithStatus: FileWithStatus[] = await Promise.all(
      filesToAdd.map(async (file, index) => {
        const result = await validateFileForModel(file, validationModelRef, activeProvider, currentCount + index);
        return {
          file,
          status: result.status,
          error: result.error
        };
      })
    );

    // Add files (both valid and invalid - user can see status)
    setAttachments(prev => [...prev, ...newFilesWithStatus]);

    // Show toast for any invalid files
    const invalidFiles = newFilesWithStatus.filter(f => f.status === 'invalid');
    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles.map(f => f.error).filter(Boolean);
      if (errorMessages.length > 0) {
        antdMessage.warning({
          content: t('fileUpload.errors.someFilesInvalid', {
            count: invalidFiles.length,
            defaultValue: `${invalidFiles.length} file(s) cannot be uploaded with this model`
          }),
          duration: 4
        });
      }
    }
  };

  /**
   * Handle file input change
   */
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await processFiles(files);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Drag & Drop handlers
   */
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    // Only show drop overlay for file drags, not text selection drags
    const hasFiles = Array.from(e.dataTransfer.items).some(item => item.kind === 'file');
    if (hasFiles) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    if (disableAttachments || sendDisabled) return;

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processFiles(files);
    }
  };

  /**
   * Remove attachment
   */
  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * Detect if text is RTL (Hebrew)
   */
  const isRTL = (text: string): boolean => {
    const hebrewRegex = /[\u0590-\u05FF]/;
    return hebrewRegex.test(text);
  };

  // While the box has text, its direction follows what was TYPED — that is
  // what lets a Hebrew speaker drop an English phrase mid-sentence and have
  // both read correctly. While it is EMPTY there is nothing to detect, and
  // falling through to 'ltr' put the Hebrew placeholder on the left of an
  // otherwise right-to-left page.
  const inputDirection = message ? (isRTL(message) ? 'rtl' : 'ltr') : direction;

  return (
    <div
      className={`chat-input-wrapper ${isDragging ? 'dragging' : ''} ${isEditing ? 'editing' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Edit mode indicator */}
      {isEditing && (
        <div className="edit-mode-indicator">
          <svg className="edit-mode-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
          </svg>
          <button
            className="cancel-edit-btn"
            onClick={onCancelEdit}
            type="button"
            title={t('chat.cancelEdit')}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Drop zone overlay */}
      {!disableAttachments && (
        <div className={`drop-overlay ${isDragging ? 'visible' : ''}`}>
          <div className="drop-overlay-content">
            <svg className="drop-icon" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48" />
            </svg>
            <span className="drop-text">{t('fileUpload.dropHere')}</span>
          </div>
        </div>
      )}

      {/* File Attachments Preview — Anthropic-style cards. Same component
          rendered in MessageItem after send, so the visual treatment is
          consistent before and after send. */}
      {(attachments.length > 0 || driveFiles.length > 0) && (
        <div className="attachment-card-row">
          {attachments.map((att, index) => (
            <FileAttachmentCard
              key={index}
              mode="preview"
              file={att.file}
              fileName={att.file.name}
              fileSize={att.file.size}
              fileType={att.file.type}
              status={att.status}
              errorText={att.error}
              onRemove={() => removeAttachment(index)}
            />
          ))}
          {driveFiles.map((df) => (
            <FileAttachmentCard
              key={`drive-${df.file_id}`}
              mode="preview"
              fileName={df.name}
              fileSize={df.size ?? undefined}
              fileType={df.mime_type}
              source={{ provider: 'google_drive', file_id: df.file_id, web_view_link: df.web_view_link }}
              onRemove={() => setDriveFiles((prev) => prev.filter((x) => x.file_id !== df.file_id))}
            />
          ))}
        </div>
      )}

      {/* "Add from Google Drive" — picked files become chips above; nothing is
          fetched until send. */}
      {!disableAttachments && driveEnabled && (
        <DriveFilePicker
          open={drivePickerOpen}
          alreadyAttached={driveFiles.map((f) => f.file_id)}
          maxSelectable={Math.max(0, maxFiles - attachments.length - driveFiles.length)}
          onAdd={(files) => setDriveFiles((prev) => {
            const seen = new Set(prev.map((f) => f.file_id));
            return [...prev, ...files.filter((f) => !seen.has(f.file_id))];
          })}
          onClose={() => setDrivePickerOpen(false)}
        />
      )}

      {/* Two-row input layout: textarea on top, tools + send on bottom */}
      <div className="chat-input-group">
        {/* Row 1: Text Input */}
        <div className="chat-input-row-text chat-input-mirror-host">
          <textarea
            ref={textareaRef}
            className={`chat-input ${inputDirection === 'rtl' ? 'rtl-input' : 'ltr-input'}`}
            // Suppress the "Ask me anything…" placeholder while live
            // transcription is painting into the mirror overlay —
            // otherwise the dim italic transcript visually overlaps
            // the placeholder and reads as garbled text on top of
            // garbled text.
            placeholder={(interimText || pendingFinals) ? '' : t('chat.inputPlaceholder')}
            value={message}
            onChange={(e) => {
              // If the user starts typing while the mic is live, stop
              // recording so they can edit freely. Their next press of
              // the mic button will resume from the current caret
              // position (handleVoiceTranscript inserts at selection).
              if (voice.state === 'listening') voice.stop();
              setMessage(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              // iOS Safari scrolls the document to bring the focused input
              // into view — with our fixed layout the chat never needs to
              // move, so force it back to top on the next frame. Without
              // this the page jumps and the chat disappears behind the
              // keyboard on first tap.
              requestAnimationFrame(() => {
                window.scrollTo(0, 0);
                if (document.scrollingElement) {
                  document.scrollingElement.scrollTop = 0;
                }
              });
            }}
            disabled={textareaDisabled}
            dir={inputDirection}
            rows={1}
            autoCapitalize="sentences"
            autoCorrect="on"
            spellCheck
          />
          {/* Live-transcription mirror overlay.
              Rendered while EITHER buffer is non-empty:
                - pendingFinals: chunks the recogniser already finalised,
                  but we hold them in italic until the user stops.
                - interimText: the in-flight chunk.
              Both render in the same italic+dimmed style; user can't
              tell them apart, which is what we want — the boundary is
              an implementation detail. Layout-wise the mirror has
              identical font/padding/line-height/width to the textarea
              (CSS) so the suffix lines up after the committed value
              with correct wrapping. Committed text is visibility:hidden
              to occupy its pixel footprint without re-rendering it. */}
          {(interimText || pendingFinals) && (
            <div
              ref={mirrorRef}
              className={`chat-input-mirror ${inputDirection === 'rtl' ? 'rtl-input' : 'ltr-input'}`}
              dir={inputDirection}
              aria-hidden="true"
            >
              <span className="chat-input-mirror-committed">
                {/* Trailing space when needed so the italic suffix
                    doesn't run into the committed text (e.g.
                    "hello" + "world" → "hello world"). */}
                {message}
                {message && !/\s$/.test(message) ? ' ' : ''}
              </span>
              <span className="chat-input-mirror-interim">
                {pendingFinals}
                {pendingFinals && interimText ? ' ' : ''}
                {interimText}
              </span>
            </div>
          )}
        </div>

        {/* Row 2: Tools left, Send center-right */}
        <div className="chat-input-row-tools">
          {/* Left tools */}
          <div className="input-tools-left">
            {/* Attachment button */}
            {!disableAttachments && (
              <>
                <AttachMenu
                  disabled={sendDisabled}
                  driveEnabled={driveEnabled}
                  onUploadFromComputer={() => fileInputRef.current?.click()}
                  onAddFromDrive={() => setDrivePickerOpen(true)}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept={acceptAttribute || undefined}
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                />
              </>
            )}

            {/* Model picker — icon-only trigger, elegant popover. Rendered
                immediately (the trigger is just an icon), so it appears
                instantly instead of popping in after the async model fetch.
                The list inside the popover fills in once `availableModels`
                arrives — no remount, no layout shift while typing. Absent
                when this conversation's final list holds a single model:
                there is nothing to switch to. */}
            {modelPickerShown && (
              <ModelSelector
                models={availableModels}
                selectedModel={selectedModel}
                onModelChange={onModelChange}
                disabled={sendDisabled}
                journeyDetection={journeyDetection}
              />
            )}

            {/* Artifact branding toggles — a sibling of the model picker,
                same trigger/popover design. The parent supplies it only when
                the agent + tenant + brand book conditions all hold, so
                rendering here is unconditional beyond presence. */}
            {artifactSettings && artifactSettings.formats.length > 0 && (
              <ArtifactSettings
                formats={artifactSettings.formats}
                branding={artifactSettings.branding}
                onToggle={artifactSettings.onToggle}
                disabled={sendDisabled}
              />
            )}

            {connectors}

            {/* Mode/challenge-level chip — mobile only (CSS-gated). When
                the bot has a mode slider configured AND the tenant has
                `agents.behavior_switching` on, this chip appears next to
                the model picker; tapping it opens an upward popover with
                the full mode list + descriptions. Desktop continues to use
                the inline slider in the header above the messages.
                The behavior_switching feature flag is the existing gate
                for mode switching everywhere — this surface inherits it. */}
            {behaviorSwitchingEnabled && modeConfig?.enabled && conversationIdForMode &&
              typeof activeModeIndex === 'number' && onModeChange && (
              <ModeMenuButton
                config={modeConfig}
                conversationId={conversationIdForMode}
                activeIndex={activeModeIndex}
                onModeChange={onModeChange}
                disabled={sendDisabled}
              />
            )}
          </div>

          {/* Send / Stop button — voice mic sits to the LEFT of the
              send arrow on the right edge (Whisper-style placement),
              hidden in the streaming state because no new input is
              accepted then. */}
          <div className="input-send-area">
            {contextMeter}
            {voiceInputEnabled && !isStreaming && voice.isSupported && (
              <VoiceInputButton
                state={voice.state}
                levelsRef={voice.levelsRef}
                durationSeconds={voice.durationSeconds}
                start={voice.start}
                stop={voice.stop}
                disabled={sendDisabled}
              />
            )}
            {/* Stop sitting BESIDE Send (not replacing it) — for a composer that
                stays open for queued messages while a turn runs. Right next to
                the send arrow, where the hand already is. */}
            {showStopBesideSend && onStopGeneration && (
              <button
                className="stop-button stop-button--beside"
                onClick={onStopGeneration}
                title={t('chat.stop')}
                aria-label={t('chat.stop')}
                data-testid="aoa-stop-button"
                type="button"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="3" />
                </svg>
              </button>
            )}
            {isStreaming ? (
              <button
                className="stop-button"
                onClick={onStopGeneration}
                title={t('chat.stop')}
                type="button"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="4" width="16" height="16" rx="2" />
                </svg>
              </button>
            ) : (
              <button
                className="send-button"
                onClick={handleSend}
                disabled={sendDisabled || (!message.trim() && attachments.length === 0 && driveFiles.length === 0) || hasInvalidFiles}
                title={hasInvalidFiles ? t('fileUpload.errors.fixFilesFirst', { defaultValue: 'Remove invalid files to send' }) : t('chat.send')}
                type="button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
